import { Injectable } from '@angular/core';
import {
  AssetGroups,
  FundamentalData,
  SecuritySymbol,
  Watchlist,
  WatchlistItem,
  WatchlistItemResponse,
  WatchlistResponse,
  WatchlistQuote,
  WatchlistResponseHoldings
} from '../../domain/public-api';
import { TickerSymbolStatus } from '../../features/ticker-symbol/ticker-symbol.domain';
import { WatchlistUtils } from './watchlist-utils';

/**
 * Convert watchlist backend JSON-parsed data into frontend types.
 */
@Injectable({
  providedIn: 'root'
})
export class WatchlistMapperService {

  constructor() {}

  /**
   * Convert array of backend watchlists into Typescript types.
   */
  mapAllWatchlists(watchlistsResponse: Array<WatchlistResponse>): Array<Watchlist> {
    const result: Array<Watchlist> = [];
    watchlistsResponse?.forEach((watchlist: WatchlistResponse): void => {
      const watchlistConverted: Watchlist = this.mapWatchlist(watchlist, false, false);
      result.push(watchlistConverted);
    });
    return WatchlistUtils.sortWatchlists(result);
  }

  /**
   * Convert a single watchlist backend object to a Typescript type.
   */
  mapWatchlist(watchlistResponse: WatchlistResponse, hasQuote: boolean = false, hasFundamentals: boolean = false): Watchlist {
    if (watchlistResponse === undefined || watchlistResponse.holdings === undefined) {
      return {} as Watchlist;
    }
    const result: Watchlist = WatchlistUtils.copy(watchlistResponse, ['holdings']);
    result.assetGroups = this.mapToAssetGroups(watchlistResponse.holdings);
    result.hasQuote = hasQuote;
    result.hasFundamentals = hasFundamentals;
    return result;
  }

  private mapToAssetGroups(assetGroupsResponse: WatchlistResponseHoldings): AssetGroups<WatchlistItem> {
    const assetGroups: AssetGroups<WatchlistItem> = new AssetGroups<WatchlistItem>();
    for (const assetGroup in assetGroupsResponse) {
      if (Object.getOwnPropertyDescriptor(assetGroupsResponse, assetGroup)) {
        for (const convertedItem of assetGroupsResponse[assetGroup]) {
          const mappedItem = this.mapWatchlistItem(convertedItem);
          if (mappedItem !== undefined) {
            assetGroups.add(mappedItem);
          }
        }
      }
    }
    return assetGroups;
  }

  /**
   * Create a WatchlistItem instance.
   */
  private mapWatchlistItem(watchlistItemResponse: WatchlistItemResponse): WatchlistItem {
    if (watchlistItemResponse === undefined) {
      return undefined;
    }
    const symbol = {

    } as SecuritySymbol;

    if (watchlistItemResponse.id !== undefined) { symbol.id = watchlistItemResponse.id; }
    if (watchlistItemResponse.market !== undefined) { symbol.market = watchlistItemResponse.market; }
    if (watchlistItemResponse.marketType !== undefined) { symbol.marketType = watchlistItemResponse.marketType; }
    if (watchlistItemResponse.symbolType !== undefined) { symbol.symbolType = watchlistItemResponse.symbolType; }
    if (watchlistItemResponse.displaySymbol !== undefined) { symbol.displaySymbol = watchlistItemResponse.displaySymbol; }
    if (watchlistItemResponse.tickerSymbol !== undefined) { symbol.tickerSymbol = watchlistItemResponse.tickerSymbol; }
    if (watchlistItemResponse.call !== undefined) { symbol.call = watchlistItemResponse.call; }
    if (watchlistItemResponse.optionExpired !== undefined) { symbol.optionExpired = watchlistItemResponse.optionExpired; }
    if (watchlistItemResponse.symbolError !== undefined) { symbol.symbolError = watchlistItemResponse.symbolError; }
    if (watchlistItemResponse.valid !== undefined) { symbol.valid = watchlistItemResponse.valid; }

    const quote: WatchlistQuote = Object.assign({}, watchlistItemResponse);

    // make sure we populate the same boolean field as in recent quotes mapper
    // backend domain: Holding.java#halted
    if (watchlistItemResponse.halted !== undefined && quote.securityHalted === undefined) {
      quote.securityHalted = watchlistItemResponse.halted;
    }

    quote.logoLight = watchlistItemResponse.logoLight;

    const fundamentalData: FundamentalData =
      (watchlistItemResponse.fundamentalData !== undefined) ? Object.assign({}, watchlistItemResponse.fundamentalData) : undefined;

    const tickerSymbolStatus: TickerSymbolStatus = WatchlistUtils.toSymbolStatus(quote, symbol);

    return {
      symbol,
      quote,
      fundamentalData,
      tickerSymbolStatus
    } as WatchlistItem;
  }
}
